import {
  DeliveryContainerDto,
  KnownDeliveryContainerDtoAvailability,
  KnownDeliveryContainerDtoStatus,
  DeliveryContainerHubDto,
  KnownDeliveryContainerDtoType,
  DeliveryContainerModuleDto,
  Position2D,
  Dimensions3D,
  KnownHubDetailsDtoType,
  KnownDeliveryDtoDeliveryType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { mapDeliveryTransactionTypeToProjectModule } from '../../project-modules/mappings';
import { BoolNo } from '../../../ui/components/pure/icons/BoolNo.icon';
import { BoolYes } from '../../../ui/components/pure/icons/BoolYes.icon';
import { Deleted } from '../../../ui/components/pure/icons/Deleted.icon';
import { Suspend } from '../../../ui/components/pure/icons/Suspend.icon';
import { SVGIconComponent } from '../../../ui/components/pure/icons/types';
import { Colors } from '../../../ui/theme/colors';
import { PartyModel } from './party.model';

export class DeliveryContainer implements DeliveryContainerDto {
  public readonly id: string;
  public readonly isKiosk: boolean;
  public readonly type: KnownDeliveryContainerDtoType;
  public readonly module: DeliveryContainerModuleDto;
  public readonly status: KnownDeliveryContainerDtoStatus;
  public readonly name: string;
  public readonly availability: KnownDeliveryContainerDtoAvailability;
  public readonly createdTime: Date;
  public readonly isConsideredLow?: boolean;
  public readonly hub: DeliveryContainerHubDto;
  public readonly position: Position2D;
  public readonly innerDimensions?: Dimensions3D;
  public readonly outerDimensions: Dimensions3D;
  public readonly uiPosition: Position2D;
  public readonly uiOuterDimensions: Dimensions3D;
  public readonly selectedInternalProjectModules: string[] = [];
  public readonly preferredInternalProjectModules: string[] = [];
  public readonly selectedExternalProjectModules: string[] = [];
  public readonly preferredExternalProjectModules: string[] = [];
  public readonly reservedForRecipients: PartyModel[] = [];

  constructor(
    deliveryContainer: Omit<DeliveryContainerDto, 'type' | 'module'>,
    type: KnownDeliveryContainerDtoType,
    module: DeliveryContainerModuleDto
  ) {
    this.id = deliveryContainer.id;
    this.isKiosk = deliveryContainer.isKiosk;
    this.type = type;
    this.module = module;
    this.status = deliveryContainer.status as KnownDeliveryContainerDtoStatus;
    this.name = deliveryContainer.name;
    this.availability = deliveryContainer.availability as KnownDeliveryContainerDtoAvailability;
    this.createdTime = deliveryContainer.createdTime;
    this.isConsideredLow = deliveryContainer.isConsideredLow;
    this.hub = deliveryContainer.hub;
    this.position = deliveryContainer.position;
    this.innerDimensions = deliveryContainer.innerDimensions;
    this.outerDimensions = deliveryContainer.outerDimensions;
    this.uiPosition = deliveryContainer.uiPosition;
    this.uiOuterDimensions = deliveryContainer.uiOuterDimensions;
    this.selectedInternalProjectModules = deliveryContainer.selectedInternalProjectModules || [];
    this.selectedExternalProjectModules = deliveryContainer.selectedExternalProjectModules || [];
    this.reservedForRecipients = (deliveryContainer.reservedForRecipients || []).map((party) => new PartyModel(party));
  }

  public get highlightColor() {
    if (this.status === KnownDeliveryContainerDtoStatus.Disabled) {
      return Colors.cancelledDeletedRed;
    }
    switch (this.availability) {
      case KnownDeliveryContainerDtoAvailability.Available:
        return '#008807';
      case KnownDeliveryContainerDtoAvailability.InUse:
        return Colors.deliveredBlue;
      case KnownDeliveryContainerDtoAvailability.Reserved:
        return '#8F9090';
      default:
        return undefined;
    }
  }

  public get availabilityColor() {
    if (this.isKiosk) {
      return '#FFFFFF';
    }

    if (this.status === KnownDeliveryContainerDtoStatus.Disabled) {
      switch (this.hub.type) {
        case KnownHubDetailsDtoType.LockersHubExternal:
          return '#E6E8EA';
        default:
          return '#FAE0E0';
      }
    }

    if (this.reservedForRecipients.length > 0) {
      return '#E6E8EA';
    }

    switch (this.availability) {
      case KnownDeliveryContainerDtoAvailability.Available:
        return '#FFFFFF';
      case KnownDeliveryContainerDtoAvailability.InUse:
        return '#DAE3ED';
      case KnownDeliveryContainerDtoAvailability.Reserved:
        return '#E6E8EA';
      default:
        return undefined;
    }
  }

  public get availabilityBadgeColor() {
    if (
      this.status === KnownDeliveryContainerDtoStatus.Active &&
      this.availability === KnownDeliveryContainerDtoAvailability.InUse
    ) {
      return {
        bgColor: Colors.deliveredBlue,
        color: 'white'
      };
    } else {
      return {
        bgColor: 'white',
        color: Colors.bookedScannedInProgressGray
      };
    }
  }

  public get availabilityIcon() {
    const map: Record<KnownDeliveryContainerDtoAvailability, SVGIconComponent> = {
      [KnownDeliveryContainerDtoAvailability.Available]: BoolYes,
      [KnownDeliveryContainerDtoAvailability.InUse]: BoolNo,
      [KnownDeliveryContainerDtoAvailability.Reserved]: Suspend
    };
    return map[this.availability];
  }

  public get availabilityDescription() {
    if (this.isKiosk) {
      return 'Kiosk';
    }
    switch (this.status) {
      case KnownDeliveryContainerDtoStatus.Disabled:
        return 'Disabled';
      case KnownDeliveryContainerDtoStatus.Active:
        switch (this.availability) {
          case KnownDeliveryContainerDtoAvailability.Available:
            return 'Available';
          case KnownDeliveryContainerDtoAvailability.InUse:
            return 'In Use';
          case KnownDeliveryContainerDtoAvailability.Reserved:
            return 'Reserved';
          default:
            return '';
        }
      default:
        return '';
    }
  }

  public get statusColor() {
    const map: Record<KnownDeliveryContainerDtoStatus, string> = {
      [KnownDeliveryContainerDtoStatus.Active]: Colors.collectedGreen,
      [KnownDeliveryContainerDtoStatus.Disabled]: Colors.cancelledDeletedRed,
      [KnownDeliveryContainerDtoStatus.Deleted]: Colors.overdueOrange
    };
    return map[this.status];
  }

  public get statusIcon() {
    const map: Record<KnownDeliveryContainerDtoStatus, SVGIconComponent> = {
      [KnownDeliveryContainerDtoStatus.Active]: BoolYes,
      [KnownDeliveryContainerDtoStatus.Disabled]: BoolNo,
      [KnownDeliveryContainerDtoStatus.Deleted]: Deleted
    };
    return map[this.status];
  }

  public acceptsDeliveryTransactionType(type: KnownDeliveryDtoDeliveryType): boolean {
    const mapping = mapDeliveryTransactionTypeToProjectModule(type);
    if (!mapping) return false;
    if (this.selectedInternalProjectModules.length === 0 && this.selectedExternalProjectModules.length === 0) {
      return true;
    }
    if (mapping.type === 'internal') {
      return this.selectedInternalProjectModules.includes(mapping.name);
    }
    return this.selectedExternalProjectModules.includes(mapping.name);
  }
}
